import { z } from 'zod';
import { IsoDateCodec, NullableIsoDateCodec } from './date-codecs.js';
import { UuidSchema } from './primitives/uuid.js';

// ============================================================================
// Admin API Keys - List all API keys
// ============================================================================

export const AdminApiKeyOwnerSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
});

export const AdminApiKeySchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  scopes: z.array(z.string()),
  capability: z.enum(['admin', 'public']),
  ownerUserId: z.string(),
  createdByUserId: z.string(),
  createdAt: IsoDateCodec,
  updatedAt: IsoDateCodec,
  expiresAt: IsoDateCodec,
  revokedAt: NullableIsoDateCodec,
  revokedByUserId: z.string().nullable(),
  keyPrefix: z.string(),
  keySuffix: z.string(),
  preview: z.string(),
  owner: AdminApiKeyOwnerSchema.nullable(),
  lastUsedAt: NullableIsoDateCodec,
  status: z.enum(['active', 'expired', 'revoked']),
});

export const AdminApiKeysListResponseSchema = z.object({
  keys: z.array(AdminApiKeySchema),
});

export type AdminApiKeyOwner = z.infer<typeof AdminApiKeyOwnerSchema>;
export type AdminApiKey = z.infer<typeof AdminApiKeySchema>;
export type AdminApiKeysListResponse = z.infer<typeof AdminApiKeysListResponseSchema>;

// ============================================================================
// Admin API Keys - Create new API key
// ============================================================================

export const AdminApiKeyCreateBodySchema = z.object({
  label: z.string().min(1),
  description: z.string().optional(),
  expiresIn: z.string().optional(),
  scopes: z.array(z.string()).optional(),
  capability: z.enum(['admin', 'standard', 'public']).optional(),
  ownerUserId: z.string().optional(),
});

export const AdminApiKeyCreateResponseSchema = z.object({
  apiKey: AdminApiKeySchema,
  secret: z.string(),
});

export type AdminApiKeyCreateBody = z.infer<typeof AdminApiKeyCreateBodySchema>;
export type AdminApiKeyCreateResponse = z.infer<typeof AdminApiKeyCreateResponseSchema>;

// ============================================================================
// Admin API Keys - Delegated key (GET/POST)
// ============================================================================

export const AdminApiKeyDelegatedKeySchema = z.object({
  id: z.string(),
  label: z.string(),
  createdAt: IsoDateCodec,
  expiresAt: IsoDateCodec,
  preview: z.string(),
  scopes: z.array(z.string()).optional(),
  capability: z.string().optional(),
});

export const AdminApiKeyDelegatedGetResponseSchema = z.object({
  key: AdminApiKeyDelegatedKeySchema.nullable(),
});

export const AdminApiKeyDelegatedCreateResponseSchema = z.object({
  secret: z.string(),
  key: AdminApiKeyDelegatedKeySchema,
});

export type AdminApiKeyDelegatedKey = z.infer<typeof AdminApiKeyDelegatedKeySchema>;
export type AdminApiKeyDelegatedGetResponse = z.infer<typeof AdminApiKeyDelegatedGetResponseSchema>;
export type AdminApiKeyDelegatedCreateResponse = z.infer<typeof AdminApiKeyDelegatedCreateResponseSchema>;

// ============================================================================
// Admin API Keys - Delete/Revoke API key
// ============================================================================

export const AdminApiKeyDeleteParamsSchema = z.object({
  id: UuidSchema,
});

export const AdminApiKeyDeleteResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    revokedAt: NullableIsoDateCodec,
  }),
});

export type AdminApiKeyDeleteParams = z.infer<typeof AdminApiKeyDeleteParamsSchema>;
export type AdminApiKeyDeleteResponse = z.infer<typeof AdminApiKeyDeleteResponseSchema>;
